import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";

import { useI18n, useTranslations, type SupportedLocale } from "@/i18n";
import type { MessageId } from "@/i18n/catalogs/en";
import { useAuth } from "@/hooks/useAuth";

const userFacingLocales = [
  "en",
  "es",
  "de",
  "fr",
] as const satisfies readonly SupportedLocale[];
type UserFacingLocale = (typeof userFacingLocales)[number];

const localeMessageIds: Record<UserFacingLocale, MessageId> = {
  en: "common.locale.en",
  es: "common.locale.es",
  de: "common.locale.de",
  fr: "common.locale.fr",
};

const localePresentation: Record<
  UserFacingLocale,
  { flag: string; shortLabel: string }
> = {
  en: { flag: "🇬🇧", shortLabel: "EN" },
  es: { flag: "🇪🇸", shortLabel: "ES" },
  de: { flag: "🇩🇪", shortLabel: "DE" },
  fr: { flag: "🇫🇷", shortLabel: "FR" },
};

export interface LanguageSelectorProps {
  className?: string;
  iconOnly?: boolean;
  onLocaleChange?: () => void;
  openUpward?: boolean;
  persistToAccount?: boolean;
}

export function LanguageSelector({
  className = "",
  iconOnly = false,
  onLocaleChange,
  openUpward = false,
  persistToAccount = false,
}: LanguageSelectorProps) {
  const { locale, setLocale } = useI18n();
  const { updateCurrentUser } = useAuth();
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [persistenceError, setPersistenceError] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectedLocale: UserFacingLocale =
    locale === "es" || locale === "de" || locale === "fr" ? locale : "en";

  useEffect(() => {
    if (!isOpen) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    const closeOnPointerDown = (event: PointerEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", closeOnPointerDown);
    return () => document.removeEventListener("pointerdown", closeOnPointerDown);
  }, [isOpen]);

  const close = (restoreFocus = false): void => {
    setIsOpen(false);
    if (restoreFocus) window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const selectLocale = (nextLocale: SupportedLocale): void => {
    setLocale(nextLocale);
    close(true);
    setPersistenceError(false);
    onLocaleChange?.();
    if (persistToAccount) {
      void updateCurrentUser({ ui_locale: nextLocale }).catch(() => {
        setPersistenceError(true);
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (!isOpen && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen || !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      return;
    }
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ??
        []
    );
    if (!items.length) return;
    event.preventDefault();
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? items.length - 1
          : event.key === "ArrowDown"
            ? (currentIndex + 1) % items.length
            : (currentIndex - 1 + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return (
    <div
      className={`group relative ${className}`}
      onKeyDown={handleKeyDown}
      ref={containerRef}
    >
      {!iconOnly && (
        <p className="mb-1.5 text-xs font-medium text-muted">
          {t("settings.language.uiLocale")}
        </p>
      )}
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`${t("settings.language.uiLocale")}: ${t(localeMessageIds[selectedLocale])}`}
        className={
          iconOnly
            ? "flex h-11 w-11 items-center justify-center rounded-lg text-ink transition-colors hover:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            : "flex min-h-11 w-full items-center gap-2.5 rounded-lg border border-line bg-surface px-3 text-left text-sm font-medium text-ink shadow-sm transition hover:border-muted hover:bg-canvas/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
        }
        onClick={() => setIsOpen((open) => !open)}
        ref={triggerRef}
        type="button"
      >
        <span aria-hidden="true" className="text-lg leading-none">
          {localePresentation[selectedLocale].flag}
        </span>
        {!iconOnly && (
          <>
            <span className="flex-1">{t(localeMessageIds[selectedLocale])}</span>
            <FiChevronDown
              aria-hidden="true"
              className={`h-4 w-4 text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </>
        )}
      </button>
      {iconOnly && !isOpen && (
        <span
          className={`pointer-events-none absolute left-1/2 z-[70] -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 text-xs font-medium text-surface opacity-0 shadow-sm transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 ${openUpward ? "bottom-[calc(100%+0.35rem)]" : "top-[calc(100%+0.35rem)]"}`}
          role="tooltip"
        >
          {t("settings.language.uiLocale")}
        </span>
      )}
      {isOpen && (
        <div
          aria-label={t("settings.language.uiLocale")}
          className={`absolute z-50 rounded-xl border border-line bg-surface p-1.5 shadow-soft motion-safe:animate-fade-in ${iconOnly ? "left-0 w-44" : "left-0 right-0"} ${openUpward ? "bottom-[calc(100%+0.5rem)]" : "top-[calc(100%+0.5rem)]"}`}
          ref={menuRef}
          role="menu"
        >
          {userFacingLocales.map((value) => (
            <button
              aria-checked={selectedLocale === value}
              className={`flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm transition-colors focus:outline-none focus-visible:bg-accent/10 focus-visible:text-highlight ${selectedLocale === value ? "bg-canvas font-semibold text-ink" : "text-body hover:bg-accent/10 hover:text-highlight"}`}
              key={value}
              onClick={() => selectLocale(value)}
              role="menuitemradio"
              type="button"
            >
              <span aria-hidden="true" className="text-lg leading-none">
                {localePresentation[value].flag}
              </span>
              <span className="flex-1">{t(localeMessageIds[value])}</span>
              <span className="text-xs font-semibold text-muted">
                {localePresentation[value].shortLabel}
              </span>
              {selectedLocale === value && (
                <FiCheck aria-hidden="true" className="h-4 w-4 text-ink" />
              )}
            </button>
          ))}
        </div>
      )}
      {persistenceError && (
        <p className="sr-only" role="alert">
          {t("common.saveError")}
        </p>
      )}
    </div>
  );
}
